<template>
  <PostList
    :reverse="true"
    :entity-id="eventId"
    :total-posts-count="totalPostsCount"
    :can-create-posts="canCreatePosts"
    :post-form-warning="postFormWarning"
    :view-context="viewContext"
    :load-posts="loadEventPosts"
    :perform-create-post="performCreatePost"
    :perform-update-post="performUpdatePost"
    :perform-delete-post="performDeletePost"
  />
</template>

<script lang="ts">
import { defineComponent, inject, PropType, provide } from "vue"
import PostList, {
  CreatePostOfEntityFunction,
  DeletePostOfEntityFunction,
  UpdatePostOfEntityFunction,
} from "features/posts/PostList.vue"
import { ViewContext } from "concerns/useContextName"
import { eventsApi } from "store/api"
import { ResponseError } from "interfaces/error.interface"
import { LoadMoreFunction } from "features/posts/PostOmissionLoader.vue"
import { GalleryService } from "features/gallery/service/gallery.service"
import { PostThumbnailService } from "features/posts/gallery/post-thumbnail.service"

export default defineComponent({
  name: "EventPostList",
  components: { PostList },
  props: {
    totalPostsCount: { type: Number, required: true },
    eventId: { type: Number, required: true },
    canCreatePosts: { type: Boolean, default: false },
    postFormWarning: { type: String, default: null },
    canEditHighlights: { type: Boolean, default: false },
    viewContext: { type: Object as PropType<ViewContext> },
  },
  setup(props) {
    const performCreatePost: CreatePostOfEntityFunction = async (
      eventId: number,
      postText: string
    ) => {
      try {
        const { data } = await eventsApi.createEventPost(eventId.toString(), {
          text: postText,
        })
        return data
      } catch (error: ResponseError) {
        console.error(error)
        return null
      }
    }

    const performUpdatePost: UpdatePostOfEntityFunction = async (
      eventId: number,
      postId: number,
      postText: string
    ) => {
      try {
        const { data } = await eventsApi.updateEventPost(
          eventId.toString(),
          postId.toString(),
          {
            text: postText,
          }
        )
        return data
      } catch (error: ResponseError) {
        console.error(error)
        return null
      }
    }

    const performDeletePost: DeletePostOfEntityFunction = async (
      eventId: number,
      postId: number
    ) => {
      try {
        await eventsApi.deleteEventPost(eventId.toString(), postId.toString())
        return true
      } catch (error: ResponseError) {
        console.error(error)
        return false
      }
    }

    const loadEventPosts: LoadMoreFunction = (offset: number, limit: number) =>
      eventsApi
        .getEventPosts(props.eventId.toString(), offset, limit, "desc")
        .then((response) => response.data)

    const galleryService: GalleryService = inject("GalleryService")
    if (galleryService) {
      const thumbnailService = new PostThumbnailService(galleryService)
      provide("ThumbnailService", thumbnailService)
    }

    return {
      performCreatePost,
      performUpdatePost,
      performDeletePost,
      loadEventPosts,
    }
  },
})
</script>
